import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import { Button } from "@2u-landing-pages/components/Button";
import { usePageState } from "@2u-landing-pages/providers";
import { TypeBannerWithoutUnresolvableLinksResponse, TypeFormWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Drawer as BricksDrawer, ButtonGroup, Container } from "@red-digital/bricks";
import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { Banner } from "../../../Banner";
import { Form } from "../../../Form";
import { DrawerProps } from "../../Drawer.types";

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getTabbableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((el) => {
    if (el.closest("[hidden], [inert]")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === "hidden" || style.display === "none") return false;
    return true;
  });
}

// @ts-ignore
import styles from "./BottomDrawer.module.scss";

const componentMap = {
  form: Form,
};

interface MultiProgramDrawerProps {
  children?: React.ReactNode;
  topSlot?: React.ReactNode;
}

const MultiProgramDrawer: React.FC<MultiProgramDrawerProps> = ({ children, topSlot }) => {
  return React.isValidElement(children) ? React.cloneElement(children, { topSlot } as any) : children;
};

export const BottomDrawer: React.FC<DrawerProps> = ({ fields, inspector = () => null, isLoading, phoneNumber, children }) => {
  const { state, dispatch } = usePageState();
  const { closeButton, banner, template } = fields || {};
  const Component = children
    ? MultiProgramDrawer
    : componentMap[(template?.sys?.contentType?.sys?.id || "") as keyof typeof componentMap];
  const drawerOpenRef = useRef(state.isDrawerOpen);
  drawerOpenRef.current = state.isDrawerOpen;

  useEffect(() => {
    if (state.isDrawerOpen && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [state.isDrawerOpen]);

  useEffect(() => {
    if (!state.isDrawerOpen) return;

    const dialog = document.querySelector<HTMLDialogElement>('dialog[data-testid="drawer"]');
    if (!dialog) return;

    const getTabbables = () => getTabbableElements(dialog);

    const moveFocusInside = () => {
      const tabbables = getTabbables();
      if (tabbables.length > 0) {
        tabbables[0].focus({ preventScroll: true });
      }
    };

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(moveFocusInside);
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const tabbables = getTabbables();
      if (tabbables.length === 0) return;

      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const active = document.activeElement;
      const activeInside = active instanceof Node && dialog.contains(active);

      if (event.shiftKey) {
        if (!activeInside || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!activeInside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusInCapture = (event: FocusEvent) => {
      if (!drawerOpenRef.current) return;
      const target = event.target;
      if (!(target instanceof Node) || dialog.contains(target)) return;
      const tabbables = getTabbables();
      if (tabbables.length === 0) return;
      tabbables[0].focus({ preventScroll: true });
    };

    dialog.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusInCapture, true);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      dialog.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusInCapture, true);
    };
  }, [state.isDrawerOpen]);

  const closeDrawer = useCallback(() => {
    EventClient.applicationClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: "Drawer",
        position: "Background",
      },
    });
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch]);

  useEffect(() => {
    if (!state.isDrawerOpen) return;
    const dialog = document.querySelector<HTMLDialogElement>('dialog[data-testid="drawer"]');
    if (!dialog) return;

    const onCancel = (event: Event) => {
      event.preventDefault();
      closeDrawer();
    };

    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [state.isDrawerOpen, closeDrawer]);

  useEffect(() => {
    if (!state.isDrawerOpen) return;
    const dialog = document.querySelector<HTMLDialogElement>('dialog[data-testid="drawer"]');
    if (!dialog) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !dialog.contains(target)) return;

      const validationRoot = target.closest("#__validation_errors");
      if (!validationRoot) return;

      requestAnimationFrame(() => {
        validationRoot.scrollIntoView({ block: "nearest", behavior: "auto", inline: "nearest" });
        if (target !== validationRoot) {
          target.scrollIntoView({ block: "nearest", behavior: "auto", inline: "nearest" });
        }
      });
    };

    dialog.addEventListener("focusin", onFocusIn, true);
    return () => dialog.removeEventListener("focusin", onFocusIn, true);
  }, [state.isDrawerOpen]);

  const handleMultiProgramClose = useCallback(() => {
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch]);

  const handleSingleProgramClose = useCallback(() => {
    if (closeButton?.fields?.action === "CLOSE_DRAWER") {
      dispatch({ type: closeButton.fields.action });
    }
  }, [dispatch, closeButton?.fields?.action]);

  const topSlot = useMemo(() => {
    if (state.drawerType === "multi-program" || (!closeButton?.fields && children)) {
      return (
        <Container>
          <ButtonGroup
            className={styles.multiProgramClose}
            placement="center"
            breakpoints={{ md: { placement: "right", layout: "inline" } }}
          >
            <Button v2 size="large" variant="plain" iconSelection={{ icon: "x" }} onClick={handleMultiProgramClose} location="Drawer">
              Close
            </Button>
          </ButtonGroup>
        </Container>
      );
    }

    if (closeButton && closeButton?.fields) {
      return (
        <ButtonGroup placement="center" className={styles.buttonWrapper}>
          <Button
            variant={closeButton?.fields?.variation}
            color={closeButton?.fields?.color}
            size={closeButton?.fields?.size}
            iconSelection={closeButton?.fields?.iconSelection}
            onClick={handleSingleProgramClose}
            location="Drawer"
          >
            {closeButton?.fields?.label}
          </Button>
        </ButtonGroup>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.drawerType, closeButton?.fields, children, handleMultiProgramClose, handleSingleProgramClose]);

  const bannerType = banner?.sys?.contentType?.sys?.id;

  return (
    <Fragment>
      {banner && bannerType === "banner" && (
        <Banner
          className={styles.banner}
          {...(banner as TypeBannerWithoutUnresolvableLinksResponse)?.fields}
          inspector={inspector}
          phoneNumber={phoneNumber}
          location="Drawer"
          stripFocusContext="banner"
        />
      )}
      {banner && bannerType === "automated_content" && (
        <AutomatedContentBanner
          className={styles.banner}
          {...banner?.fields}
          phoneNumber={phoneNumber}
          location="Drawer"
          stripFocusContext="banner"
        />
      )}
      {Component && (
        <BricksDrawer className={styles.drawer} isOpen={state.isDrawerOpen} onClose={closeDrawer} position="bottom">
          <Component
            {...(children ? { children } : (template?.fields as TypeFormWithoutUnresolvableLinksResponse["fields"]))}
            inDrawer={!children}
            isLoading={isLoading}
            topSlot={topSlot}
            phoneNumber={phoneNumber}
            {...(Component === Form && { location: "Drawer" })}
          />
        </BricksDrawer>
      )}
    </Fragment>
  );
};
